import { ArrowLeftRight, Calendar, CircleHelp, Check, FileText } from "lucide-react"
import type { ReactNode } from "react"

import type { ComparePage } from "../../content/compare"
import { ShieldIllustration } from "../home/home-art"
import { formatCheckedOn } from "../home/home-content"
import { HeroBand } from "../landing/page-parts"
import { IconTile } from "../landing/product-art"

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
/** Footnote markers are tiny text: pad them to a 24x24 px touch target (WCAG 2.5.8). */
const citeClass = `${linkClass} inline-flex min-h-6 min-w-6 items-center justify-center`

/** In-page source markers: `[S1, S2]`, each a link to the dated source list. */
export const Cites = ({ ids }: { ids: readonly string[] }) => (
  <sup className="text-muted-foreground">
    [
    {ids.map((id, i) => (
      <span key={id}>
        {i > 0 && ", "}
        <a href={`#${id.toLowerCase()}`} className={citeClass}>
          {id}
        </a>
      </span>
    ))}
    ]
  </sup>
)

/** A "Yes..." cell gets a check; the words stay, the icon is decorative. */
const Mark = ({ text }: { text: string }) =>
  /^Yes\b/.test(text) ? (
    <span className="inline-flex items-start gap-1.5">
      <Check aria-hidden="true" className="text-success mt-0.5 size-4 shrink-0" />
      <span>{text}</span>
    </span>
  ) : (
    <>{text}</>
  )

/** What the source marker and the date mean, above a table. */
export const Legend = ({
  checked,
  notStated = false,
}: {
  checked: string
  notStated?: boolean
}) => (
  <ul
    aria-label="How to read the table"
    className="border-border bg-card text-muted-foreground flex flex-wrap gap-x-5 gap-y-2 rounded-lg border px-4 py-3 text-sm"
  >
    <li className="inline-flex items-center gap-2">
      <b className="text-foreground">[S1]</b>
      <span>Source marker, listed under Sources</span>
    </li>
    {notStated && (
      <li className="inline-flex items-center gap-2">
        <CircleHelp aria-hidden="true" className="text-primary size-4" />
        <span>
          <b className="text-foreground">Not stated</b>: not found on its pages
        </span>
      </li>
    )}
    <li className="inline-flex items-center gap-2">
      <Calendar aria-hidden="true" className="text-primary size-4" />
      <span>Date we opened each page: {checked}</span>
    </li>
  </ul>
)

const HOW = [
  {
    icon: FileText,
    title: "Their pages only",
    body: "Every cell about another app comes from that company's own pricing, features, help or store page.",
  },
  {
    icon: Calendar,
    title: "Dated",
    body: "Each cell has a source and the date we opened it. We re-check every 90 days.",
  },
  {
    icon: CircleHelp,
    title: "Not stated",
    body: "If we cannot find a claim, we write “not stated”. We never guess.",
  },
] as const

/** The method in three points beside a decorative shield. */
export const HowWeCheck = () => (
  <section
    aria-labelledby="how-we-check"
    className="border-border bg-card mx-band my-6 grid items-center gap-6 rounded-xl border p-6 md:max-w-5xl md:grid-cols-[minmax(0,18rem)_1fr]"
  >
    <div className="hidden justify-self-center md:block">
      <ShieldIllustration />
    </div>
    <div className="grid gap-3">
      <h2 id="how-we-check" className="text-2xl font-semibold">
        How we check
      </h2>
      <ul className="grid gap-3">
        {HOW.map(({ icon, title, body }) => (
          <li key={title} className="flex items-start gap-3">
            <IconTile icon={icon} />
            <div>
              <b>{title}.</b> <span className="text-muted-foreground">{body}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

interface CompareTableProps {
  caption: string
  page: ComparePage
  oursHeader: string
  theirsHeader: string
}

/** Our column holds published features only; a row without `ours` shows "See our pricing page". */
export const CompareTable = ({ caption, page, oursHeader, theirsHeader }: CompareTableProps) => (
  <div
    role="region"
    aria-label={caption}
    tabIndex={0}
    className="border-border bg-card focus-visible:ring-ring overflow-x-auto rounded-lg border outline-none focus-visible:ring-2"
  >
    <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr className="bg-muted">
          <td className="w-1/4" />
          <th scope="col" className="px-4 py-3 text-[13px] font-semibold">
            {oursHeader}
          </th>
          <th scope="col" className="px-4 py-3 text-[13px] font-semibold">
            {theirsHeader}
          </th>
        </tr>
      </thead>
      <tbody>
        {page.rows.map((row) => (
          <tr key={row.label} className="border-border border-t">
            <th scope="row" className="px-4 py-3 align-top font-medium">
              {row.label}
            </th>
            <td className="px-4 py-3 align-top">
              {row.ours ? (
                <Mark text={row.ours} />
              ) : (
                <a href="/pricing" className={linkClass}>
                  See our pricing page
                </a>
              )}
            </td>
            <td className="px-4 py-3 align-top">
              <Mark text={row.theirs.value} />
              {row.theirs.quote && (
                <>
                  {" "}
                  <q>{row.theirs.quote}</q>
                </>
              )}{" "}
              <Cites ids={row.theirs.sources} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/** Page and address of every source, with the date it was opened. */
export const SourceList = ({ page }: { page: ComparePage }) => (
  <ol aria-label="Sources" className="grid gap-2 text-sm">
    {Object.entries(page.sources).map(([id, s]) => (
      <li key={id} id={id.toLowerCase()} className="scroll-mt-20">
        <b>{id}</b> {s.label},{" "}
        <a href={s.url} rel="noopener noreferrer" className={`${linkClass} break-all`}>
          {s.url}
        </a>
        . Opened {formatCheckedOn(s.checkedAt)}.
      </li>
    ))}
  </ol>
)

interface CompareHeaderProps {
  reviewed: string
  title: string
  children: ReactNode
}

export const CompareHeader = ({ reviewed, title, children }: CompareHeaderProps) => (
  <HeroBand>
    <p className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
      <span className="bg-primary/10 text-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold">
        <ArrowLeftRight aria-hidden="true" className="size-3.5" />
        Comparison
      </span>
      <span>Last reviewed {formatCheckedOn(reviewed)}</span>
    </p>
    <h1 className="text-3xl font-bold tracking-tight md:text-5xl">{title}</h1>
    <div className="max-w-3xl">{children}</div>
  </HeroBand>
)

export const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="px-band py-6">
    <div className="grid max-w-3xl gap-3">
      <h2 className="text-2xl font-semibold">{title}</h2>
      {children}
    </div>
  </section>
)

export { linkClass }
